import { safelyParseJson } from '@olivierpascal/helpers';

import type { IMakeRequired } from '~/helpers/types';
import type { IStorage, IStorageOptions } from './types';
import { defaultOptions } from './types';

export class ClientStorage implements IStorage {
  private options: IMakeRequired<IStorageOptions, 'prefix'>;
  private readonly namespacedPrefix: string;

  public constructor(
    private storage: Storage & {
      allKeys?(): Array<string>;
    },
    options?: IStorageOptions,
  ) {
    this.options = { ...defaultOptions, ...options };
    this.namespacedPrefix = this.options.namespace
      ? `${this.options.prefix}${this.options.namespace}`
      : this.options.prefix;
  }

  private uriFromKey(key: string): string {
    return `${this.namespacedPrefix}::${key}`;
  }

  private keyFromUri(uri: string): string {
    return uri.substring(`${this.namespacedPrefix}::`.length);
  }

  public set<T>(key: string, value: T): void {
    const uri = this.uriFromKey(key);
    if (value === undefined) {
      this.storage.removeItem(uri);

      return;
    }

    this.storage.setItem(uri, JSON.stringify(value));
  }

  public get<T>(key: string): T | undefined {
    const uri = this.uriFromKey(key);
    const json = this.storage.getItem(uri);
    if (!json) {
      return undefined;
    }

    const payload = safelyParseJson<T>(json);

    return payload;
  }

  public delete(key: string): void {
    const uri = this.uriFromKey(key);
    this.storage.removeItem(uri);
  }

  public allKeys(): Array<string> {
    return (
      this.storage?.allKeys?.() ??
      Object.keys(this.storage).filter((key) =>
        key.startsWith(this.namespacedPrefix),
      )
    ).map((key) => this.keyFromUri(key));
  }

  public clear(): void {
    this.allKeys().map((key) => this.delete(key));
  }
}
